/**
 * MusicGptVoicePicker — Phase 2.5 voice library picker.
 *
 * Renders a searchable autocomplete that lets users pick a specific
 * MusicGPT voice clone (artist-like singer) for their song. Shown only
 * when the Create page's engine is set to MUSICGPT.
 *
 * Selecting a voice sets `voice_id` (UUID) on the parent state; the
 * backend forwards it to MusicGPT's /MusicAI submission.
 *
 * IMPLEMENTATION NOTE: The popover is rendered via a React portal to
 * `document.body` and positioned with fixed coordinates derived from
 * the trigger's bounding rect. This avoids the z-stacking issue where
 * the next sibling card (with its own stacking context) was occluding
 * the absolutely-positioned popover (iteration_30 regression).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Mic, Search, X, Loader2, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MusicGptVoicePicker({ value, onChange, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [voices, setVoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedName, setSelectedName] = useState('');
  const [anchorRect, setAnchorRect] = useState(null);
  const inputRef = useRef(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  const updateAnchor = useCallback(() => {
    if (triggerRef.current) {
      setAnchorRect(triggerRef.current.getBoundingClientRect());
    }
  }, []);

  // Close on outside click / scroll / resize.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      const t = e.target;
      if (
        (triggerRef.current && triggerRef.current.contains(t)) ||
        (popoverRef.current && popoverRef.current.contains(t))
      ) return;
      setOpen(false);
    };
    const onReposition = () => updateAnchor();
    document.addEventListener('mousedown', onDocClick);
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [open, updateAnchor]);

  // Fetch voices when popover opens or query changes (debounced).
  useEffect(() => {
    if (!open) return;
    const handle = setTimeout(() => {
      setLoading(true);
      const params = new URLSearchParams({ limit: '50', page: '0' });
      if (query.trim()) params.set('query', query.trim());
      fetch(`${API}/musicgpt/voices?${params}`)
        .then((r) => (r.ok ? r.json() : { voices: [], total: 0 }))
        .then((data) => {
          setVoices(Array.isArray(data.voices) ? data.voices : []);
          setTotal(Number(data.total) || 0);
        })
        .catch(() => {
          setVoices([]);
          setTotal(0);
        })
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(handle);
  }, [open, query]);

  // Resolve the display name when a `value` is set externally.
  useEffect(() => {
    if (!value) {
      setSelectedName('');
      return;
    }
    const inList = voices.find((v) => v.voice_id === value);
    if (inList) setSelectedName(inList.voice_name || '');
  }, [value, voices]);

  const buttonLabel = useMemo(() => {
    if (value && selectedName) return selectedName;
    if (value) return 'Voice selected';
    return 'Auto (let MusicGPT pick)';
  }, [value, selectedName]);

  const handleSelect = (voice) => {
    onChange?.(voice.voice_id, voice.voice_name);
    setSelectedName(voice.voice_name || '');
    setOpen(false);
    setQuery('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange?.(null, '');
    setSelectedName('');
    setQuery('');
  };

  const togglePopover = () => {
    if (disabled) return;
    updateAnchor();
    setOpen((v) => !v);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  return (
    <div className="relative" data-testid="musicgpt-voice-picker">
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={togglePopover}
        className={`w-full h-[46px] rounded-lg bg-black/20 border-2 border-white/10 hover:border-white/20 flex items-center px-4 transition ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        data-testid="musicgpt-voice-picker-trigger"
      >
        <Mic className="w-4 h-4 mr-2 text-white/70" />
        <span className="text-white/90 text-sm font-medium mr-2">Voice:</span>
        <span className={`flex-1 text-left text-sm truncate ${value ? 'text-emerald-300' : 'text-white/60'}`}>
          {buttonLabel}
        </span>
        {value && (
          <X
            className="w-4 h-4 ml-2 text-white/60 hover:text-white"
            onClick={handleClear}
            data-testid="musicgpt-voice-picker-clear"
          />
        )}
      </button>

      {open && anchorRect && createPortal(
        <div
          ref={popoverRef}
          style={{
            position: 'fixed',
            top: anchorRect.bottom + 8,
            left: anchorRect.left,
            width: anchorRect.width,
            zIndex: 9999,
          }}
          className="max-h-[420px] overflow-hidden rounded-lg bg-zinc-900 border border-white/10 shadow-2xl"
          data-testid="musicgpt-voice-picker-popover"
        >
          <div className="p-2 border-b border-white/10 flex items-center gap-2 bg-black/40">
            <Search className="w-4 h-4 text-white/50" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search 2000+ voices (e.g. Drake, Bruno Mars)…"
              className="flex-1 bg-transparent text-white text-sm placeholder:text-white/40 outline-none"
              data-testid="musicgpt-voice-picker-search"
            />
            {loading && <Loader2 className="w-4 h-4 animate-spin text-white/50" />}
          </div>

          <div className="max-h-[360px] overflow-y-auto">
            <button
              type="button"
              onClick={() => handleSelect({ voice_id: null, voice_name: '' })}
              className="w-full text-left px-4 py-2.5 text-sm text-white/80 hover:bg-white/5 border-b border-white/5 flex items-center gap-2"
              data-testid="musicgpt-voice-picker-auto"
            >
              <span className="text-white/60">✨ Auto</span>
              <span className="text-white/40 text-xs">Let MusicGPT choose based on the prompt</span>
            </button>

            {voices.length === 0 && !loading && (
              <div className="px-4 py-6 text-center text-sm text-white/50">
                {query ? `No voices matching "${query}"` : 'No voices loaded yet'}
              </div>
            )}

            {voices.map((v) => {
              const isSelected = v.voice_id === value;
              return (
                <button
                  key={v.voice_id}
                  type="button"
                  onClick={() => handleSelect(v)}
                  className={`w-full text-left px-4 py-2.5 text-sm hover:bg-white/5 flex items-center justify-between border-b border-white/5 ${isSelected ? 'bg-emerald-500/10 text-emerald-200' : 'text-white/90'}`}
                  data-testid={`musicgpt-voice-option-${v.voice_id}`}
                >
                  <span className="truncate">{v.voice_name || 'Untitled voice'}</span>
                  {isSelected && <Check className="w-4 h-4 text-emerald-400" />}
                </button>
              );
            })}
          </div>

          {total > voices.length && (
            <div className="px-4 py-2 text-xs text-white/40 bg-black/40 border-t border-white/10">
              Showing {voices.length} of {total} — refine your search to narrow results.
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
